import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { getMyStandin, setMyAway } from '@stepflow/ui-shared/services/dataPlaneApi';
import useStandin from '@stepflow/ui-shared/hooks/useStandin.js';
import StandinSettingsPanel from '@stepflow/ui-shared/components/standin/StandinSettingsPanel.jsx';
import { personLabel } from '@stepflow/ui-shared/utils/personLabel.js';

/**
 * PROFILE (STAND-IN.md §9) — from the account menu. Tabs: Profile (who you are) · Stand-in (the control centre):
 * the master switch "Answer for me" (off pauses every stand-in of yours, everywhere) and every room you set a stand-in
 * up in — each expands to the SAME settings the room's 🤝 shows. To add a room: open it and use 🤝.
 */
function RoomRow({ room, me }) {
  const [open, setOpen] = useState(false);
  const st = useStandin(open ? room.networkId : '', room.poolId, me, [], 0);
  const s = room.setting || {};
  return (
    <li className="rounded-lg border border-gray-200" data-testid={`profile-standin-room-${room.networkId}-${room.poolId}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className="inline-flex min-w-0 flex-1 items-center gap-1.5 text-left">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" aria-hidden />}
          <span className="truncate text-[13px] font-medium text-gray-900">{room.poolId}</span>
          <span className="truncate text-[11px] text-gray-500">· {room.networkId}</span>
        </button>
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${s.enabled ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
          {s.enabled ? `On · ${(s.agentIds || []).length} agent${(s.agentIds || []).length === 1 ? '' : 's'}` : 'Off'}
        </span>
        {room.waiting ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">{room.waiting} waiting</span> : null}
        <a href={`/agentic?networkId=${encodeURIComponent(room.networkId)}&poolId=${encodeURIComponent(room.poolId)}`}
          title="Open the room" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      </div>
      {open ? <div className="border-t border-gray-100 px-3 py-2"><StandinSettingsPanel standin={st} /></div> : null}
    </li>
  );
}

export default function ProfileDialog({ user, onClose, initialTab = 'profile' }) {
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState({ loaded: false, away: true, rooms: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (tab !== 'standin' || data.loaded) return;
    getMyStandin().then((r) => setData({ loaded: true, away: r?.away !== false, rooms: r?.rooms || [] }))
      .catch((e) => { setData((d) => ({ ...d, loaded: true })); setError((e && e.message) || "Couldn't load your stand-in."); });
  }, [tab, data.loaded]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const toggleAway = async (v) => {
    setBusy(true); setError('');
    try { const r = await setMyAway(v); setData((d) => ({ ...d, away: r?.away !== false })); } catch (e) {
      setError((e && e.message) || "Couldn't save that.");
    }
    setBusy(false);
  };
  const me = user?.sub || user?.userId || '';
  const tabBtn = (k, label) => (
    <button type="button" data-testid={`profile-tab-${k}`} onClick={() => setTab(k)} aria-selected={tab === k} role="tab"
      className={`px-3 py-1.5 text-[13px] font-medium transition-colors ${tab === k ? 'border-b-2 border-indigo-600 text-indigo-700' : 'text-gray-500 hover:text-gray-800'}`}>
      {label}
    </button>
  );
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true"
      aria-label="Profile" data-testid="profile-dialog">
      <div className="flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-gray-200 px-5 pt-3">
          <div className="flex flex-1" role="tablist">{tabBtn('profile', 'Profile')}{tabBtn('standin', 'Stand-in')}</div>
          <button type="button" onClick={onClose} aria-label="Close" className="mb-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100">
            <X className="h-[1.125rem] w-[1.125rem]" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-[13px]">
          {tab === 'profile' ? (
            <dl className="space-y-2" data-testid="profile-info">
              <div><dt className="text-[11px] text-gray-500">Name</dt><dd className="text-gray-900">{personLabel({ name: user?.name, email: user?.email })}</dd></div>
              <div><dt className="text-[11px] text-gray-500">Email</dt><dd className="text-gray-900">{user?.email || ''}</dd></div>
            </dl>
          ) : (
            <div data-testid="profile-standin">
              <label className="mb-3 flex items-start gap-2 rounded-lg bg-indigo-50/60 px-3 py-2.5">
                <input type="checkbox" data-testid="profile-standin-away" checked={data.away} disabled={busy || !data.loaded}
                  onChange={(e) => toggleAway(e.target.checked)} className="mt-0.5" />
                <span>
                  <span className="block font-semibold text-gray-900">Answer for me</span>
                  <span className="block text-[12px] text-gray-600">When you&apos;re mentioned, an agent you supervise answers on your behalf —
                    always labelled, and pending your confirmation when it isn&apos;t sure. Off pauses every room at once.</span>
                </span>
              </label>
              {!data.loaded ? <p className="text-gray-400">Loading…</p> : data.rooms.length ? (
                <ul className="space-y-2" data-testid="profile-standin-rooms">
                  {data.rooms.map((r) => <RoomRow key={`${r.networkId}/${r.poolId}`} room={r} me={me} />)}
                </ul>
              ) : (
                <p className="text-gray-500" data-testid="profile-standin-empty">No rooms yet — open a room where you supervise an agent and use 🤝 at the top of the message box.</p>
              )}
              {error ? <p className="mt-2 text-[12px] text-rose-600">{error}</p> : null}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
